import React, { useEffect, useRef, useState } from 'react';
import { Text, View, StyleSheet, Image } from '@react-pdf/renderer';
import { colors } from '../../common/pallette/colors';
import { useTranslation } from 'react-i18next';
import { TimelineSelectorOptions } from '../../api/types';
import { blankUser, jlLogo, jlLogoFull, starImageData } from './images';
import { sortCriteria, isOdd } from './utils';
import { formatDate, genUID, getAverageSiteStatus, siteReviewStatus } from '../../common/StaticExports';
import { baseUrl } from '../../api/config';
import { useSelector } from 'react-redux';
import { Font } from '@react-pdf/renderer';
import RavenLogo from '../../assets/images/RavenLogo.png';
import RavenNewLogo from '../../assets/images/RavenNewLogo.png';
import JLogo from '../../assets/images/JLogo.png';
import RavenFrontPage from '../../assets/images/RavenFrontPage.png';
// @ts-ignore
import fontLight from './NotoSansJP-Light.otf';
// @ts-ignore
import fontRegular from './NotoSansJP-Regular.otf';
// @ts-ignore
import fontBold from './NotoSansJP-Bold.otf';


// 本サイトで使⽤している⽂章‧画像等の無断での複製‧転載を禁⽌します。

export const Header: React.FC<{ imageUrl: string }> = ({ imageUrl }) => (
  <View
    fixed
    style={{
      height: 40,
      marginBottom: 20,
      borderBottom: '0.2px solid rgba(0,0,0,0.2)',
    }}
  >
    <Image src={jlLogo} style={{ position: 'absolute', left: -2, top: 15, height: 20 }} />
    <Image src={imageUrl} style={{ position: 'absolute', right: 0, top: 15, height: 18 }} />
  </View>
);

// export const Footer = () => (
//   <View
//     style={{ flex: 1, position: 'absolute', bottom: 0, width: '100%' }}
//     fixed
//     render={({ pageNumber, totalPages }) => {
//       return (
//         <View style={{ position: 'absolute', bottom: 20, flex: 1, left: 30, right: -30 }}>
//           <View style={{ height: 0.5, backgroundColor: 'black', marginBottom: 2 }} />
//           <Text style={{ fontFamily: 'NotoSansJP', textAlign: 'center', fontSize: 7 }}>
//             本サイトで使⽤している⽂章‧画像等の無断での複製‧転載を禁⽌します。©JAPAN PROFESSIONAL FOOTBALL LEAGUE ALL
//             RIGHTS RESERVED.
//           </Text>
//           <Text
//             style={{ fontFamily: 'NotoSansJP', textAlign: 'center', fontSize: 7 }}
//           >{`${pageNumber} / ${totalPages}`}</Text>
//         </View>
//       );
//     }}
//   />
// );

const styles = StyleSheet.create({
  body: {
    fontFamily: 'NotoSansJP',
  },
  table: {
    margin: 10,
    fontFamily: 'NotoSansJP',
  },
  tableRow: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#ccc",
    borderBottomStyle: "solid",
    paddingVertical: 4,
    fontFamily: 'NotoSansJP',
    height: 'auto',
  },
  tableCell: {
    flex: 1,
    fontSize: 10,
    paddingHorizontal: 4,
    fontFamily: 'NotoSansJP',
  },
  offSiteCell: {
    flexDirection: "row",
    alignItems: "center",
    flex: 1,
    paddingHorizontal: 4,
  },
  statusCell: {
    flexDirection: "row",
    alignItems: "center",
    flex: 1,
  },
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 10,
    fontFamily: 'NotoSansJP',
  },
  barContainer: {
    flexDirection: 'row',
    borderRadius: 4,
    overflow: 'hidden',
    marginRight: 20,
    fontFamily: 'NotoSansJP',
  },
  notEvaluated: {
    backgroundColor: colors.grey.dark,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 2,
  },
  met: {
    backgroundColor: colors.statusColors.met,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 2,
  },
  partial: {
    backgroundColor: colors.statusColors.partial,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 2,
  },
  notMet: {
    backgroundColor: colors.statusColors.notMet,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 2,
  },
  barText: {
    color: 'white',
    fontSize: 10,
    fontWeight: 'bold',
    paddingHorizontal: 4,
    fontFamily: 'NotoSansJP',
  },
  dot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    marginRight: 10,
  },
  text: {
    fontSize: 12,
    marginRight: 5,
    fontFamily: 'NotoSansJP',
  },
  standardTag: {
    backgroundColor: "#007bff",
    color: "#fff",
    borderRadius: 12,
    paddingHorizontal: 6,
    paddingVertical: 2,
    textAlign: "center",
    alignSelf: "flex-start",
    fontSize: 8,
    margin: 5
  },
  ownerIcon: {
    backgroundColor: "#ccc",
    borderRadius: 12,
    width: 12,
    height: 12,
    textAlign: "center",
    fontSize: 10,
    lineHeight: 20,
    marginRight: 4,
  }
});

Font.register({
  family: 'NotoSansJP-Light',
  src: fontLight,
});
Font.register({
  family: 'NotoSansJP',
  src: fontRegular,
});
Font.register({
  family: 'NotoSansJP-Bold',
  src: fontBold,
});

if (localStorage.getItem('lang') === 'en') {
  Font.registerHyphenationCallback(word => [word]);
} else {
  Font.registerHyphenationCallback(word => Array.from(word).flatMap(char => [char, '']));
}

export const TopPage: React.FC<{
  name: string;
  japName: string | null;
  title: string;
  reportType: string;
  reportDate: string;
  imageUrl: string;
}> = ({ name, japName, title, reportType, reportDate, imageUrl }) => (
  <View style={{ position: 'relative', width: '100%', height: '100%' }}>
    <Image src={RavenFrontPage} 
    style={{ 
      position: 'absolute', 
      top: 0, 
      left: 0, 
      width: '100%', 
      height: '100%',
      objectFit: 'cover'
    }} />
    <View style={{ position: 'relative', padding: 50 }}>
      <View style={{ justifyContent: 'space-between', flexDirection: 'row', alignItems: 'center' }}>
        <Image style={{ height: 70 }} src={JLogo} />
        <Image style={{ height: 70 }} src={imageUrl && imageUrl !== '' ? imageUrl : RavenLogo} />
        <Image style={{ height: 70 }} src={RavenNewLogo} />
      </View>
      <Text style={{ width: 300, fontSize: 50, textTransform: 'uppercase', marginTop: 130, fontWeight: '600' }}>{title}</Text>
      <Text style={{ width: 'auto', fontSize: 20, marginTop: 30, fontWeight: '300', textTransform: 'capitalize' }}>{name}</Text>
      <Text style={{ width: 'auto', fontSize: 18, marginTop: 20, textTransform: 'uppercase', fontWeight: '200' }}>{reportDate}</Text>
      <View style={{ marginTop: 200 }}>
        <Text style={{ width: 'auto', fontSize: 14, fontWeight: '200' }}>{'Prepared by'}</Text>
        <Text style={{ width: 'auto', fontSize: 20, fontWeight: '300' }}>{'J League Evaluation Team'}</Text>
      </View>
    </View>
  </View>
);

export const BigTitle: React.FC<{ title: string; setPageNumber: (a: number) => void }> = ({ title, setPageNumber }) => {
  return (
    <>
      <View
        render={(data:any) => {
          if (data?.totalPages) {
            setPageNumber && setPageNumber(data.pageNumber);
          }
          return <View />;
        }}
      />
      <View style={{ height: '50px', borderBottom: `1.5px solid ${colors.iris}`, marginBottom: '30px' }}>
        <Text style={{ fontSize: 24, color: colors.iris, marginTop: 'auto', marginBottom: '5px' }}>{title}</Text>
      </View>
    </>
  );
};

export const SmallTitle: React.FC<{ title: string }> = ({ title }) => {
  return (
    <View style={{ marginBottom: '20px', marginTop: '25px' }}>
      <Text style={{ fontSize: 16, color: colors.iris }}>{title}</Text>
    </View>
  );
};

// export const ContentsTableItem: React.FC<{
//   title: string;
//   items?: [];
//   pageNumber?: number;
//   itemsPageNumber: number[];
// }> = ({ title, items, pageNumber, itemsPageNumber }) => {
//   return (
//     <View style={{ borderBottom: '1px solid #cccccc', paddingBottom: 5, marginTop: 3 }}>
//       <View style={{ display: 'flex', justifyContent: 'center' }}>
//         <Text style={{ fontSize: 10, fontFamily: 'NotoSansJP-Bold' }}>{title}</Text>
//         <Text style={{ fontSize: 10, position: 'absolute', right: 0, fontWeight: 'normal' }}>{pageNumber}</Text>
//       </View>
//       {items && (
//         <View style={{ marginTop: 3 }}>
//           {sortCriteria(items).map((el, idx) => (
//             <View style={{ backgroundColor: isOdd(idx) ? '#ECECEC' : 'white', paddingVertical: 2 }}>
//               <Text style={{ fontSize: 10, marginLeft: 20 }}>{`${idx + 1} ${el.Name}`}</Text>
//               <Text style={{ fontSize: 10, position: 'absolute', right: 0, fontWeight: 'normal' }}>
//                 {itemsPageNumber[idx]}
//               </Text>
//             </View>
//           ))}
//         </View>
//       )}
//     </View>
//   );
// };

export const ExecutiveSummary = (props:any) => {
  const {
    targetTitle,
    achievedTitle,
    target,
    acheived,
  } = props;

  return(
    <View style={{ flexDirection: 'row' }}>
      <View style={{ flex: 1, fontSize: 12, flexDirection: 'row' }}>
        <Text>{targetTitle} </Text> <StarRating rating={target} />
      </View>
      <View style={{ flex: 1, fontSize: 12, flexDirection: 'row' }}>
        <Text>{achievedTitle} </Text> <StarRating rating={acheived} />
      </View>
    </View>
  )
}

export const ExecOverview: React.FC<{
  seasonTitle: string;
  targetTitle: string;
  statusTitle: string;
  predictedTitle: string;
  season: number;
  target: number;
  status: string;
  predicted: number;
  timeline: number;
  IsActionPlanIncluded?: boolean;
}> = ({
  seasonTitle,
  targetTitle,
  statusTitle,
  predictedTitle,
  season,
  target,
  status,
  predicted,
  timeline,
  IsActionPlanIncluded,
}) => {
  const [t] = useTranslation();

  const TimelineSelectorOptionsOld = [
    { label: '...', value: 0 },
    { label: 'SIX_WEEKS', value: 1 },
    { label: 'SIX_MONTHS', value: 2 },
    { label: 'ONE_YEAR', value: 3 },
  ];

  const getTimelineLabel = () => {
    const idx = TimelineSelectorOptions.findIndex(el => el.value === timeline);

    if (idx === -1) {
      return t(TimelineSelectorOptionsOld[timeline].label);
    } else {
      return t(TimelineSelectorOptions[idx].label);
    }
  };

  return (
    <View>
      <View style={{ flexDirection: 'row' }}>
        <View style={{ flex: 1, fontSize: 10, flexDirection: 'row' }}>
          <Text style={{ fontFamily: 'NotoSansJP-Bold' }}>{seasonTitle} </Text>
          <Text>{season}</Text>
        </View>
        <View style={{ flexDirection: 'row', alignItems: 'center', flex: 1, fontSize: 10 }}>
          <Text style={{ fontFamily: 'NotoSansJP-Bold' }}>{targetTitle} </Text> <StarRating rating={target} />
        </View>
      </View>
      <View style={{ flexDirection: 'row' }}>
        <View style={{ flexDirection: 'row', flex: 1, fontSize: 10 }}>
          <Text style={{ fontFamily: 'NotoSansJP-Bold' }}>{statusTitle} </Text>
          <Text>{status}</Text>
        </View>
        <View style={{ flexDirection: 'row', alignItems: 'center', flex: 1, fontSize: 10 }}>
          <Text style={{ fontFamily: 'NotoSansJP-Bold' }}>{predictedTitle} </Text>
          {predicted ? <StarRating rating={predicted} /> : <Text>{t('SPECIAL_MEASURES')}</Text>}
          {IsActionPlanIncluded && (
            <Text style={{ fontSize: 10 }}> {t('WITH_ACTION_PLAN') + '(' + getTimelineLabel() + ')'}</Text>
          )}
        </View>
      </View>
    </View>
  );
};

export const StarRating: React.FC<{ rating: number }> = ({ rating }) => {
  const stars = new Array(rating).fill(1);

  return (
    <View style={{ flexDirection: 'row', alignItems: 'center' }}>
      {stars.map((_, idx) => (
        <Image key={idx} src={starImageData} style={{ height: 12, width: 12 }} />
      ))}
    </View>
  );
};

export const TextBox: React.FC<{ text: string }> = ({ text }) => {
  return (
    <View style={{ backgroundColor: '#f2f2f3', padding: 6 }}>
      <Text style={{ fontSize: 8, lineHeight: 1.8 }}>{text}</Text>
    </View>
  );
};

export const EvaluationOverview: React.FC<{
  overview: { 0: number; 1: number; 2: number; 3: number };
  showExceeded?: boolean;
  exceededCount?: number;
  labels: { [key: string]: string };
}> = ({ overview, showExceeded = false, exceededCount = 0, labels }) => {
  // 0 not Evaluated
  // 1 Not met
  // 2 Partial
  // 3 Met

  // 0: { title: labels.notEvaluatedTitle, color: 'gray' },
  // 1: { title: labels.notMetTitle, color: '#F03A47' },
  // 2: { title: labels.partialTitle, color: '#ED8A19' },
  // 3: { title: labels.metTitle, color: '#42e01e' },

  const total = overview[0] + overview[1] + overview[2] + overview[3] + exceededCount;
  const piece = 535 / total;

  return (
    <View style={{ marginBottom: 20 }}>
      {/* Exceeded */}
      {showExceeded && (
        <>
          <View style={{ flexDirection: 'row' }}>
            <Text style={{ flex: 1, fontSize: 11 }}>{labels.exceededTitle}</Text>
            <Text style={{ fontSize: 11 }}>{exceededCount}</Text>
          </View>
          <View
            style={{
              height: 5,
              backgroundColor: '#ECECEC',
              borderRadius: 20,
              overflow: 'hidden',
              marginTop: 2,
              marginBottom: 6,
            }}
          >
            <View
              style={{
                height: 5,
                backgroundColor: '#FFD700',
                borderRadius: 20,
                width: exceededCount * piece,
              }}
            />
          </View>
        </>
      )}
      {/* MET */}
      <>
        <View style={{ flexDirection: 'row' }}>
          <Text style={{ flex: 1, fontSize: 11 }}>{labels.metTitle}</Text>
          <Text style={{ fontSize: 11 }}>{overview[3]}</Text>
        </View>
        <View
          style={{
            height: 5,
            backgroundColor: '#ECECEC',
            borderRadius: 20,
            overflow: 'hidden',
            marginTop: 2,
            marginBottom: 6,
          }}
        >
          <View style={{ height: 5, backgroundColor: '#42e01e', borderRadius: 20, width: overview[3] * piece }} />
        </View>
      </>
      {/* PARTIAL */}
      <>
        <View style={{ flexDirection: 'row' }}>
          <Text style={{ flex: 1, fontSize: 11 }}>{labels.partialTitle}</Text>
          <Text style={{ fontSize: 11 }}>{overview[2]}</Text>
        </View>
        <View
          style={{
            height: 5,
            backgroundColor: '#ECECEC',
            borderRadius: 20,
            overflow: 'hidden',
            marginTop: 2,
            marginBottom: 6,
          }}
        >
          <View style={{ height: 5, backgroundColor: '#ED8A19', borderRadius: 20, width: overview[2] * piece }} />
        </View>
      </>
      {/* NOT MET */}
      <>
        <View style={{ flexDirection: 'row' }}>
          <Text style={{ flex: 1, fontSize: 11 }}>{labels.notMetTitle}</Text>
          <Text style={{ fontSize: 11 }}>{overview[1]}</Text>
        </View>
        <View
          style={{
            height: 5,
            backgroundColor: '#ECECEC',
            borderRadius: 20,
            overflow: 'hidden',
            marginTop: 2,
            marginBottom: 6,
          }}
        >
          <View style={{ height: 5, backgroundColor: '#F03A47', borderRadius: 20, width: overview[1] * piece }} />
        </View>
      </>
    </View>
  );
};

// export const EvaluationItemList: React.FC<{ items: any[]; labels: { [key: string]: string } }> = ({
//   items,
//   labels,
// }) => {
//   const [t, i18n] = useTranslation();

//   return (
//     <View style={{ marginTop: 3 }}>
//       <Text style={{ fontSize: 11, marginBottom: 5 }}>{i18n.language === 'en' ? 'EVALUATION ITEM' : '評価項⽬'}</Text>
//       {sortCriteria(items).map((el, idx) => (
//         <View
//           wrap={false}
//           style={{
//             backgroundColor: isOdd(idx) ? '#ececec' : 'white',
//             paddingVertical: 2,
//             flexDirection: 'row',
//           }}
//         >
//           <Text style={{ fontSize: 10, marginLeft: 2 }}>{`${idx + 1} ${el.Name}`}</Text>
//           <View
//             style={{
//               width: 150,
//               marginLeft: 'auto',
//             }}
//           >
//             <ResultDot value={el.Status + (el.FutureProgress?.IsExceeded ? 1 : 0)} labels={labels} />
//           </View>
//         </View>
//       ))}
//     </View>
//   );
// };

// export const ResultDot: React.FC<{ value: number; isBig?: boolean; labels: { [key: string]: string } }> = ({
//   value,
//   isBig,
//   labels,
// }) => {
//   const data = {
//     0: { title: labels.notEvaluatedTitle, color: '#c2c2c2' },
//     1: { title: labels.notMetTitle, color: '#F03A47' },
//     2: { title: labels.partialTitle, color: '#ED8A19' },
//     3: { title: labels.metTitle, color: '#42e01e' },
//     4: { title: labels.exceededTitle, color: '#FFD700' },
//   };

//   return (
//     <View wrap={false} style={{ flexDirection: 'row', alignItems: 'center' }}>
//       <View
//         style={{
//           width: isBig ? 9 : 6,
//           height: isBig ? 9 : 6,
//           borderRadius: 20,
//           marginRight: isBig ? 10 : 6,
//           marginTop: isBig ? 3 : 2,
//           backgroundColor: data[value].color,
//         }}
//       />
//       <Text style={{ fontSize: isBig ? 16 : 10 }}>{data[value].title}</Text>
//     </View>
//   );
// };

// export const SummaryTable: React.FC<{
//   borderTop?: boolean;
//   bold?: boolean;
//   criteria: string;
//   star: string | number;
//   action: string;
//   description: string;
//   starIsString?: boolean;
// }> = ({ borderTop, bold, criteria, star, starIsString, action, description }) => {
//   return (
//     <View
//       wrap={false}
//       style={{
//         flexDirection: 'row',
//         borderBottom: '1px solid black',
//         borderTop: `${borderTop ? '1' : '0'}px solid black`,
//         marginBottom: 2,
//       }}
//     >
//       <View
//         style={{
//           padding: 6,
//           borderRight: '1px solid black',
//           borderLeft: '1px solid black',
//           width: '20%',
//         }}
//       >
//         <Text
//           style={{
//             fontSize: bold ? 10 : 7,
//             fontFamily: bold ? 'NotoSansJP-Bold' : 'NotoSansJP',
//             display: 'flex',
//             flexDirection: 'row',
//             flexWrap: 'wrap',
//           }}
//         >
//           {criteria}
//         </Text>
//       </View>
//       <View style={{ padding: 6, borderRight: '1px solid black', width: '10%' }}>
//         {starIsString ? (
//           <Text style={{ fontSize: bold ? 10 : 7, fontFamily: bold ? 'NotoSansJP-Bold' : 'NotoSansJP' }}>{star}</Text>
//         ) : (
//           <StarRating rating={star} />
//         )}
//       </View>
//       <View style={{ padding: 6, borderRight: '1px solid black', width: '25%' }}>
//         <Text style={{ fontSize: bold ? 10 : 7, fontFamily: bold ? 'NotoSansJP-Bold' : 'NotoSansJP' }}>{action}</Text>
//       </View>
//       <View style={{ padding: 6, borderRight: '1px solid black', width: '45%' }}>
//         <Text style={{ fontSize: bold ? 10 : 7, fontFamily: bold ? 'NotoSansJP-Bold' : 'NotoSansJP' }}>
//           {description}
//         </Text>
//       </View>
//     </View>
//   );
// };

export const InterviewTableHead: React.FC<{
  name: string;
  date: string;
  required: string;
  conducted: string;
}> = ({ name, date, required, conducted }) => {
  return (
    <View
      wrap={false}
      style={{
        flexDirection: 'row',
        borderBottom: '1px solid black',
        borderTop: `1px solid black`,
        marginBottom: 2,
      }}
    >
      <View style={{ padding: 6, borderRight: '1px solid black', borderLeft: '1px solid black', width: '35%' }}>
        <Text style={{ fontSize: 10, fontFamily: 'NotoSansJP-Bold' }}>{name}</Text>
      </View>
      <View style={{ padding: 6, borderRight: '1px solid black', width: '15%' }}>
        <Text style={{ fontSize: 10, fontFamily: 'NotoSansJP-Bold' }}>{date}</Text>
      </View>
      <View style={{ padding: 6, borderRight: '1px solid black', width: '30%' }}>
        <Text style={{ fontSize: 10, fontFamily: 'NotoSansJP-Bold' }}>{required}</Text>
      </View>
      <View style={{ padding: 6, borderRight: '1px solid black', width: '20%' }}>
        <Text style={{ fontSize: 10, fontFamily: 'NotoSansJP-Bold' }}>{conducted}</Text>
      </View>
    </View>
  );
};
export const InterviewTableBody: React.FC<{
  name: string;
  date: string;
  required: string[];
  conducted: string[];
}> = ({ name, date, required, conducted }) => {
  return (
    <View
      wrap={false}
      style={{
        flexDirection: 'row',
        borderBottom: '1px solid black',
        borderTop: `1px solid black`,
        marginBottom: 2,
      }}
    >
      <View style={{ padding: 6, borderRight: '1px solid black', borderLeft: '1px solid black', width: '35%' }}>
        <Text style={{ fontSize: 7 }}>{name}</Text>
      </View>
      <View style={{ padding: 6, borderRight: '1px solid black', width: '15%' }}>
        <Text style={{ fontSize: 7 }}>{date}</Text>
      </View>
      <View style={{ padding: 6, borderRight: '1px solid black', width: '30%' }}>
        {required.map(el => (
          <Text style={{ fontSize: 7 }}>{el}</Text>
        ))}
      </View>
      <View style={{ padding: 6, borderRight: '1px solid black', width: '20%' }}>
        {conducted.map(el => (
          <Text style={{ fontSize: 7 }}>{el}</Text>
        ))}
      </View>
    </View>
  );
};

export const EvaluationTeamMember: React.FC<{
  displayName: string;
  email: string;
  imageBase64: string;
}> = ({ displayName, email, imageBase64 }) => {
  return (
    <View wrap={false} style={{ flexDirection: 'row', marginBottom: 20 }}>
      <Image src={imageBase64 || blankUser} style={{ width: 50, height: 50 }} />
      <View style={{ marginLeft: 20 }}>
        <Text style={{ fontSize: 12 }}>{displayName}</Text>
        <Text style={{ fontSize: 12 }}>{email}</Text>
      </View>
    </View>
  );
};

export const BottomPage: React.FC<{
  name: string;
  japName: string | null;
  title: string;
  reportType: string;
  reportDate: string;
  imageUrl: string;
}> = ({ name, japName, title, reportType, reportDate, imageUrl }) => (
  <View style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
    <View
      style={{ height: '100%', width: '30px', backgroundColor: 'gray', position: 'absolute', left: '506px' }}
    ></View>
    <View style={{ height: '100%', width: '60px', backgroundColor: 'black', marginLeft: 'auto' }}></View>
    <Image style={{ position: 'absolute', bottom: '30px', left: '30px', height: '50px' }} src={jlLogoFull} />
    <View
      style={{
        position: 'absolute',
        right: '210px',
        bottom: '90px',
        height: '1.5px',
        width: '360px',
        backgroundColor: 'black',
      }}
    />
    <View style={{ position: 'absolute', bottom: '500px', left: '30px', width: '200px' }}>
      <Text style={{ textTransform: 'uppercase' }}>{name}</Text>
      {japName && <Text style={{ textTransform: 'uppercase' }}>{japName}</Text>}
    </View>
    <Image style={{ position: 'absolute', top: '243px', left: '290px', height: '100px' }} src={imageUrl} />
    <Text style={{ position: 'absolute', bottom: '400px', left: '30px', color: colors.iris, width: '350px' }}>
      {title}
    </Text>
    <View style={{ position: 'absolute', bottom: '285px', left: '30px' }}>
      <Text style={{ fontSize: 14, color: 'rgba(0,0,0,.8)', marginTop: 8 }}>{reportType}</Text>
      <Text style={{ fontSize: 14, color: 'rgba(0,0,0,.8)', marginTop: 8 }}>{reportDate}</Text>
    </View>
  </View>
);

export const Heading = (props:any) => {
  const {
    label,
  } = props;
  return(
      <Text style={{ marginTop: 10, fontSize: 16, fontWeight: 'bold', marginBottom: 20 }}>{label}</Text>
  )
}

export const CriteriaHeading = (props:any) => {
  const {
    label,
  } = props;
  return(
      <Text style={{ marginTop: 10, fontSize: 14, fontWeight: 'bold', marginBottom: 10 }}>{label}</Text>
  )
}

export const EvaluationGroup = (props:any) => {
  const {
    reportData,
    criteria,
  } = props;
  const [statusData, setStatusData]:any = useState([]);
  const [t] = useTranslation();

  useEffect(() => {
    var notEval = 0;
    var met = 0;
    var partial = 0;
    var notMet = 0;
    const criterias = reportData?.Criteria?.filter((d:any) => d.CriteriaGroupId === criteria?.CriteriaGroupId);
    if(criterias?.length > 0) {
        var key = 'Status';
        const criteriaArr = criterias;
        criteriaArr.map((d:any) => {
        switch(d[key]){
            case 0:
            notEval++;
            break;
            case 1:
            notMet++;
            break;
            case 2:
            partial++;
            break;
            case 3:
            met++;
            break;
        }
        });
        const tempData:any = [
        {
            name: "NOT_EVALUATED",
            data: [notEval],
            dataCount: notEval,
        },
        {
            name: "MET",
            data: [met],
            dataCount:met, 
        },
        {
            name: "PARTIAL",
            data: [partial],
            dataCount: partial,
        },
        {
            name: "NOT_MET",
            data: [notMet],
            dataCount: notMet,
        }
        ];
        setStatusData(tempData);
    }
  })

  const calcOverallStatus = () => {
    const criterias = reportData?.Criteria?.filter((d:any) => d.CriteriaGroupId === criteria?.CriteriaGroupId);
    const criteriaStatuses = criterias.map((d:any) => d?.Status);
    const statusAvg:any = getAverageSiteStatus(criteriaStatuses);
    return statusAvg;
  }

  const totalWidth = 100;
  const totalCount = reportData?.Criteria?.filter((d:any) => d.CriteriaGroupId === criteria?.CriteriaGroupId)?.length;
  
  return(
    <View style={{ marginVertical: 10, flexDirection: 'row', justifyContent: 'space-between' }}>
      <Heading label={criteria?.CriteriaGroupName.toUpperCase()}/>
      <View style={styles.container}>
        <Text style={styles.text}>{t('EVALUATION')+': '}</Text>
        <View style={styles.barContainer}>
          {statusData[0]?.dataCount > 0 && <View style={[styles.notEvaluated, { width: totalCount > 0 ? (statusData[0]?.dataCount / totalCount) * totalWidth : 0 }]}><Text style={styles.barText}>{statusData[0]?.dataCount}</Text></View>}
          {statusData[1]?.dataCount > 0 && <View style={[styles.met, { width: totalCount > 0 ? (statusData[1]?.dataCount / totalCount) * totalWidth : 0 }]}><Text style={styles.barText}>{statusData[1]?.dataCount}</Text></View>}
          {statusData[2]?.dataCount > 0 && <View style={[styles.partial, { width: totalCount > 0 ? (statusData[2]?.dataCount / totalCount) * totalWidth : 0 }]}><Text style={styles.barText}>{statusData[2]?.dataCount}</Text></View>}
          {statusData[3]?.dataCount > 0 && <View style={[styles.notMet, { width: totalCount > 0 ? (statusData[3]?.dataCount / totalCount) * totalWidth : 0 }]}><Text style={styles.barText}>{statusData[3]?.dataCount}</Text></View>}
        </View>
        <View style={{ ...styles.dot, marginLeft: 10, backgroundColor: siteReviewStatus[calcOverallStatus()]?.color }} />
        <Text style={styles.text}>{t(siteReviewStatus[calcOverallStatus()].statusName)}</Text>
      </View>
    </View>
  )
}


export const CriteriaHeader = (props:any) => {
  const {
    criteria,
  } = props;
  const [statusData, setStatusData]:any = useState([]);
  const [t] = useTranslation();

  useEffect(() => {
    var notEval = 0;
    var met = 0;
    var partial = 0;
    var notMet = 0;
    const standards = criteria?.Standards;
    if(standards?.length > 0) {
        var key = 'Status';
        const criteriaArr = standards;
        criteriaArr.map((d:any) => {
        switch(d[key]){
            case 0:
            notEval++;
            break;
            case 1:
            notMet++;
            break;
            case 2:
            partial++;
            break;
            case 3:
            met++;
            break;
        }
        });
        const tempData:any = [
        {
            name: "NOT_EVALUATED",
            data: [notEval],
            dataCount: notEval,
        },
        {
            name: "MET",
            data: [met],
            dataCount:met, 
        },
        {
            name: "PARTIAL",
            data: [partial],
            dataCount: partial,
        },
        {
            name: "NOT_MET",
            data: [notMet],
            dataCount: notMet,
        }
        ];
        setStatusData(tempData);
    }
  })

  const calcOverallStatus = () => {
    const standards = criteria?.Standards;
    const standardStatuses = standards.map((d:any) => d?.Status);
    const statusAvg:any = getAverageSiteStatus(standardStatuses);
    return statusAvg;
  }

  const totalWidth = 100;
  const totalCount = criteria?.Standards?.length;
  
  return(
    <View style={{ marginVertical: 10, flexDirection: 'column', justifyContent: 'space-between' }}>
      <CriteriaHeading label={(genUID(criteria.Uid) + ' ' + criteria?.Name).toUpperCase()}/>
      <View style={styles.container}>
        <Text style={styles.text}>{t('EVALUATION')+': '}</Text>
        <View style={styles.barContainer}>
          {statusData[0]?.dataCount > 0 && <View style={[styles.notEvaluated, { width: totalCount > 0 ? (statusData[0]?.dataCount / totalCount) * totalWidth : 0 }]}><Text style={styles.barText}>{statusData[0]?.dataCount}</Text></View>}
          {statusData[1]?.dataCount > 0 && <View style={[styles.met, { width: totalCount > 0 ? (statusData[1]?.dataCount / totalCount) * totalWidth : 0 }]}><Text style={styles.barText}>{statusData[1]?.dataCount}</Text></View>}
          {statusData[2]?.dataCount > 0 && <View style={[styles.partial, { width: totalCount > 0 ? (statusData[2]?.dataCount / totalCount) * totalWidth : 0 }]}><Text style={styles.barText}>{statusData[2]?.dataCount}</Text></View>}
          {statusData[3]?.dataCount > 0 && <View style={[styles.notMet, { width: totalCount > 0 ? (statusData[3]?.dataCount / totalCount) * totalWidth : 0 }]}><Text style={styles.barText}>{statusData[3]?.dataCount}</Text></View>}
        </View>
        <Text style={styles.text}>{t('STATUS')+': '}</Text>
        <View style={{ ...styles.dot, backgroundColor: siteReviewStatus[calcOverallStatus()]?.color }} />
        <Text style={styles.text}>{t(siteReviewStatus[calcOverallStatus()]?.statusName)}</Text>
      </View>
    </View>
  )
}

//standards Table
export const StandardsTable = (props:any) => {

  const { data } = props;
  const [t] = useTranslation();
  return (
    <View style={styles.table}>
      {/* Header Row */}
      <View style={[styles.tableRow, { fontWeight: "bold" }]}>
        <Text style={styles.tableCell}>{t('ID').toUpperCase()}</Text>
        <Text style={[styles.tableCell, {width: 500}]}>{t('STANDARD').toUpperCase()}</Text>
        <Text style={styles.tableCell}>{t('OFF_SITE').toUpperCase()}</Text>
        {/* <Text style={styles.tableCell}>{t('STAR').toUpperCase()}</Text> */}
      </View>

      {/* Data Rows */}
      {data.map((row:any, i:any) => (
        <View key={i} style={styles.tableRow}>
          <Text style={styles.tableCell}>{genUID(row.Uid)}</Text>
          <Text style={[styles.tableCell, {width: 500}]}>{row.Name}</Text>
          <View style={styles.offSiteCell}>
            <View style={[styles.dot, { backgroundColor: siteReviewStatus[row.Status]?.color }]} />
            <Text style={styles.text}>{t(siteReviewStatus[row.Status].statusName)}</Text>
          </View>
          {/* <Text style={styles.tableCell}><StarRating rating={row?.StarRating} /></Text> */}
        </View>
      ))}
    </View>
  )
};

//actions table
export const ActionTable = (props:any) => {
  const { data, staffList } = props;
  const [t] = useTranslation();

  const fetchUserDetails:any = (userId:any) => {
    return staffList?.find((d:any) => d.id == userId);
  }

  return (
    <View style={styles.table}>
      {/* Header */}
      <View style={[styles.tableRow, { fontWeight: "bold" }]}>
        <Text style={styles.tableCell}>{t('ID').toUpperCase()}</Text>
        <Text style={[styles.tableCell, {width: 700}]}>{t('ACTION').toUpperCase()}</Text>
        <Text style={styles.tableCell}>{t('DESCRIPTION').toUpperCase()}</Text>
        <Text style={styles.tableCell}>{t('STANDARD').toUpperCase()}</Text>
      </View>

      {/* Data Rows */}
      {data.map((item:any, index:any) => (
        <View key={index} style={styles.tableRow}>
          <View style={[styles.statusCell, { width: 'auto' }]}>
            <Text style={{ fontSize: 10 }}>{index+1}</Text>
          </View>
          <Text style={[styles.tableCell, {width: 700}]}>{item.Name}</Text>
          <Text style={[styles.tableCell, {width: 'auto'}]}>{item.Description}</Text>
          <View style={styles.tableCell}>
            {
              item?.Standards?.length > 0 &&
              item?.Standards.map((standard:any) => {
                return (
                    <Text style={{ fontSize: 10 }}>{genUID(standard.Uid)} {standard.StandardName}</Text>
                )
              })
            }
          </View>
        </View>
      ))}
    </View>
  )
}

export const CommentWrapper = (props:any) => {
  const { data } = props;

  return (
      <View wrap={true} style={{ backgroundColor: '#f2f2f3', paddingVertical: 6, paddingHorizontal: 10, marginTop: 10, marginBottom: 10, fontSize: 10, width: '100%', minHeight: 100 }}>
        <Text>{data?.CriteriaComment}</Text>
      </View>
  )
}

//interviews Table
export const IntrerviewsTable = (props:any) => {

  const { data } = props;
  const [t] = useTranslation();
  return (
    <View style={styles.table}>
      {/* Header Row */}
      <View style={[styles.tableRow, { fontWeight: "bold" }]}>
        <Text style={styles.tableCell}>{t('ID').toUpperCase()}</Text>
        <Text style={[styles.tableCell, {width: 500}]}>{t('INTERVIEW').toUpperCase()}</Text>
        <Text style={styles.tableCell}>{t('DATE').toUpperCase()}</Text>
        <Text style={styles.tableCell}>{t('INTERVIEWEE').toUpperCase()}</Text>
        <Text style={styles.tableCell}>{t('ROLE').toUpperCase()}</Text>
      </View>

      {/* Data Rows */}
      {data.map((row:any, i:any) => (
        <View key={i} style={styles.tableRow}>
          <Text style={styles.tableCell}>{i+1}</Text>
          <Text style={[styles.tableCell, {width: 500}]}>{row.Name}</Text>
          <Text style={[styles.tableCell]}>{row?.Date ? formatDate(row?.Date) : '-'}</Text>
          <View style={styles.tableCell}>
            {
              row?.Conducted?.length > 0 &&
              row?.Conducted.map((interviewee:any) => {
                return (
                  <View style={{ flexDirection: 'row' }}>
                    <Text style={[styles.ownerIcon, { fontSize: 10 }]}>👤</Text>
                    <Text style={{ fontSize: 10 }}>{interviewee.DisplayName}</Text>
                  </View>
                )
              })
            }
          </View>
          <View style={styles.tableCell}>
            {
              row?.Conducted?.length > 0 &&
              row?.Conducted.map((interviewee:any) => {
                return (
                  <Text style={{ fontSize: 10 }}>{interviewee.Role}</Text>
                )
              })
            }
          </View>
        </View>
      ))}
    </View>
  )
};

//Evaluation Team Table
export const EvaluationTeamTable = (props:any) => {

  const { data } = props;
  const [t] = useTranslation();
  return (
    <View style={styles.table}>
      {/* Header Row */}
      <View style={[styles.tableRow, { fontWeight: "bold" }]}>
        <Text style={styles.tableCell}>{t('ID').toUpperCase()}</Text>
        <Text style={[styles.tableCell, {width: 500}]}>{t('TEAM_MEMBER').toUpperCase()}</Text>
        <Text style={styles.tableCell}>{t('ROLE').toUpperCase()}</Text>
      </View>

      {/* Data Rows */}
      {data.map((row:any, i:any) => (
        <View key={i} style={styles.tableRow}>
          <Text style={styles.tableCell}>{i+1}</Text>
          <View style={[styles.tableCell, { flexDirection: 'row' }]}>
            <Text style={[styles.ownerIcon, { fontSize: 10 }]}>👤</Text>
            <Text style={{ fontSize: 10 }}>{row?.DisplayName}</Text>
          </View>
          <Text style={styles.tableCell}>{row?.Role ? row?.Role : '-'}</Text>
        </View>
      ))}
    </View>
  )
};